import { Button, Dropdown, Table, Skeleton, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectLoading } from "../../../redux/auth/selectors";
import RiskAssessment from "../../../service/RiskAssessment";
import { handleGeneratePDF } from "../../../utils/htmltopdf";
import { exportToExcel } from "../Clients/clientByUser";
import moment from "moment";
import { getScoreText } from "../CountryRiskScores";

const DEFAULT_PAGINATION = {
  current: 1,
  pageSize: 10,
  total: 0, // Initialize total to 0
};

export const getScoreTextDownload = (score) => {
  if (score < 2) {
    return "Low";
  } else if (score < 3) {
    return "Medium";
  } else if (score < 4) {
    return "High";
  } else {
    return "Refer to MLRO";
  }
};

const ClientsReport = () => {
  const [Reports, setReports] = useState(null);
  const [priorReportsData, setPriorReportsData] = useState(null);
  const loading = useSelector(selectLoading);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const fetchReports = async (pagination = DEFAULT_PAGINATION) => {
    const response = await RiskAssessment.ClientsReport({
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    });
    console.log("**************", response);

    setReports(response.data["clients"].map((t) => ({ ...t, key: t?._id })));
    setPagination((prev) => ({ ...prev, total: response.data.total }));
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const columns = [
    {
      title: "Client Name",
      dataIndex: "name",
    },
    {
      title: "Client Group",
      dataIndex: "group",
      render: (name, record) => <span>{record.group?.name}</span>,
    },
    {
      title: "Country",
      dataIndex: "placeOfBirth",
    },
    {
      title: "Transaction Type",
      dataIndex: "transactionType",
      render: (e) => (
        <Tag color={e ? "green" : "magenta"}>{e ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Transaction/Business Activity",
      dataIndex: "businessType",
    },
    {
      title: "Source of Funds",
      dataIndex: "sourceOfFunds",
    },
    {
      title: "Source of Wealth",
      dataIndex: "sourceOfWealth",
    },
    {
      title: "Industry",
      dataIndex: "industryOccupation",
    },
    {
      title: "Status",
      dataIndex: "status",
    },
    {
      title: "High Net Worth",
      dataIndex: "isHighNetWorth",
      render: (e) => (
        <Tag color={e ? "green" : "magenta"}>{e ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Regulator Name",
      dataIndex: "regulatorName",
    },
    {
      title: "Regulation Type",
      dataIndex: "regulatorType",
    },
    {
      title: "Referred/Transferred",
      dataIndex: "referredTransferred",
    },
    {
      title: "Regulatory Filing",
      dataIndex: "regulatoryFiling",
    },
    {
      title: "Ongoing Screening on",
      dataIndex: "ongoingScreening",
      render: (e) => (
        <Tag color={e ? "green" : "magenta"}>{e ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Risk Rating",
      dataIndex: "riskRating",
      render: (_, record) => getScoreText(record.riskRating),
    },
  ];

  if (!Reports) {
    return <Skeleton active />;
  }

  return (
    <>
      <Table
        className="w-full max-w-2xl"
        columns={columns}
        dataSource={Reports}
        loading={loading}
        // pagination={true}
        footer={() => (
          <Dropdown.Button
            type="primary"
            menu={{
              items: [
                {
                  key: "export-excel",
                  label: (
                    <Button
                      onClick={async () => {
                        const total = pagination.total ?? 10;
                        const perPage = 50;
                        const segments = [];

                        for (let i = 1; i * perPage - total <= total; i++) {
                          segments.push({ current: i });
                        }
                        if (segments.length === 0)
                          segments.push({ current: 1 });

                        const reports = await Promise.all(
                          segments.map(async (segment) => {
                            const response = await RiskAssessment.ClientsReport(
                              {
                                limit: perPage,
                                offset: (segment.current - 1) * perPage,
                              }
                            );
                            return response.data["clients"].map((t) => ({
                              ...t,
                              key: t?._id,
                            }));
                          })
                        );

                        const reportsData = reports
                          .flat()
                          .sort((a, b) =>
                            moment(a.periodicReviewDate).isBefore(
                              b.periodicReviewDate
                            )
                          );
                        setPriorReportsData(reportsData);

                        exportToExcel(
                          reportsData.map((e) => ({
                            clientName: e.name,
                            clientGroupName: e.group?.name,
                            Country: e.placeOfBirth,
                            TransactionType: e.transactionType ? "Yes" : "No",
                            TransactionBusinessActivity: e.businessType,
                            SourceOfFunds: e.sourceOfFunds,
                            SourceOfWealth: e.sourceOfWealth,
                            IndustryOccupation: e.industryOccupation,
                            status: e.status,
                            isHighNetWorth: e.isHighNetWorth ? "Yes" : "No",
                            regulatorName: e.regulatorName,
                            RegulationType: e.regulatorType,
                            referredTransferred: e.referredTransferred,
                            regulatoryFiling: e?.regulatoryFiling?.join(", "),
                            ongoingScreening: e.ongoingScreening ? "Yes" : "No",
                            RiskRating: getScoreTextDownload(e.riskRating),
                          }))
                        );
                      }}
                    >
                      Excel
                    </Button>
                  ),
                },
                {
                  key: "export-pdf",
                  label: (
                    <Button
                      onClick={async () => {
                        const total = pagination.total ?? 10;
                        const perPage = 50;
                        const segments = [];

                        for (let i = 1; i * perPage - total <= total; i++) {
                          segments.push({ current: i });
                        }
                        if (segments.length === 0)
                          segments.push({ current: 1 });

                        const reports = await Promise.all(
                          segments.map(async (segment) => {
                            const response = await RiskAssessment.ClientsReport(
                              {
                                limit: perPage,
                                offset: (segment.current - 1) * perPage,
                              }
                            );

                            return response.data.clients.map((t) => ({
                              ...t,
                              key: t?._id,
                            }));
                          })
                        );

                        const reportsData = reports
                          .flat()
                          .sort((a, b) =>
                            moment(a.periodicReviewDate).isBefore(
                              b.periodicReviewDate
                            )
                          );
                        setPriorReportsData(reportsData);

                        handleGeneratePDF({
                          header: "Reports",
                          ContentColumn: columns.filter(
                            (c) => c.dataIndex !== "group"
                          ),
                          DataSource: reportsData.map((e) => ({
                            ...e,
                            ClientGroup: e.group?.name,
                          })),
                        });
                      }}
                    >
                      PDF
                    </Button>
                  ),
                },
              ],
            }}
            arrow={{
              pointAtCenter: true,
            }}
          >
            Export
          </Dropdown.Button>
        )}
        pagination={{
          current: pagination.current, // Current page
          pageSize: pagination.pageSize, // Number of items per page
          total: pagination.total, // Total count
          showSizeChanger: true, // Option to change page size
          pageSizeOptions: ["10", "20", "50", "100"], // Available page sizes
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`, // Display total items
          onChange: (page, pageSize) => {
            // Update the current page in the pagination state
            setPagination((prev) => ({ ...prev, current: page, pageSize }));
            fetchReports({ ...pagination, current: page, pageSize });
          },
        }}
      />
    </>
  );
};

export default ClientsReport;
